import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, Trash2, Save } from "lucide-react";
import { adminBrokers, adminCreateBroker, adminUpdateBroker, adminDeleteBroker } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";

const inputCls = "w-full bg-ink-800 border border-white/10 rounded px-2 py-1 text-sm text-white";

export default function BrokersTab() {
  const qc = useQueryClient();
  const { toast } = useToast();
  const { data, isLoading } = useQuery({ queryKey: ["admin-brokers"], queryFn: adminBrokers });

  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-brokers"] });
  const createMut = useMutation({ mutationFn: adminCreateBroker, onSuccess: () => { refresh(); toast({ title: "Broker added" }); },
    onError: () => toast({ title: "Could not add broker", variant: "destructive" }) });
  const updateMut = useMutation({ mutationFn: ({ id, body }) => adminUpdateBroker(id, body), onSuccess: () => { refresh(); toast({ title: "Broker updated" }); } });
  const deleteMut = useMutation({ mutationFn: adminDeleteBroker, onSuccess: () => { refresh(); toast({ title: "Broker deleted" }); } });

  const [draft, setDraft] = useState({ name: "", website: "", servers: "" });
  const addBroker = () => {
    if (!draft.name.trim()) return;
    createMut.mutate({
      name: draft.name.trim(), website: draft.website.trim(), logo_url: "",
      servers: draft.servers.split(",").map((s) => s.trim()).filter(Boolean), status: "active",
    });
    setDraft({ name: "", website: "", servers: "" });
  };

  if (isLoading) return <Loader2 className="size-5 animate-spin text-white/50" />;

  return (
    <div className="space-y-5" data-testid="admin-brokers-tab">
      <div className="rounded-xl border border-white/10 bg-white/5 p-4 grid sm:grid-cols-4 gap-3 items-end" data-testid="broker-add-form">
        <label className="text-xs text-white/60">Name
          <input data-testid="broker-name-input" className={inputCls} value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="GTCFX" />
        </label>
        <label className="text-xs text-white/60">Website
          <input data-testid="broker-website-input" className={inputCls} value={draft.website} onChange={(e) => setDraft({ ...draft, website: e.target.value })} placeholder="https://…" />
        </label>
        <label className="text-xs text-white/60">Servers (comma-sep)
          <input data-testid="broker-servers-input" className={inputCls} value={draft.servers} onChange={(e) => setDraft({ ...draft, servers: e.target.value })} placeholder="Srv-Live, Srv-Demo" />
        </label>
        <button data-testid="broker-add-btn" onClick={addBroker} disabled={createMut.isPending}
          className="bg-amber-400/90 text-ink-900 text-xs font-semibold rounded px-3 py-1.5 inline-flex items-center justify-center gap-1">
          <Plus className="size-3.5" /> Add broker
        </button>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {(data?.brokers || []).map((b) => (
          <BrokerCard key={b.id} broker={b}
            onSave={(body) => updateMut.mutate({ id: b.id, body })}
            onDelete={() => deleteMut.mutate(b.id)} />
        ))}
      </div>
    </div>
  );
}

function BrokerCard({ broker, onSave, onDelete }) {
  const [name, setName] = useState(broker.name);
  const [website, setWebsite] = useState(broker.website || "");
  const [servers, setServers] = useState((broker.servers || []).join(", "));
  const [status, setStatus] = useState(broker.status);
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 px-4 py-4 space-y-2" data-testid={`broker-card-${broker.id}`}>
      <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} />
      <input className={inputCls} value={website} onChange={(e) => setWebsite(e.target.value)} placeholder="website" />
      <input className={inputCls} value={servers} onChange={(e) => setServers(e.target.value)} placeholder="servers" />
      <label className="flex items-center gap-2 text-xs text-white/60">
        <input type="checkbox" checked={status === "active"} onChange={(e) => setStatus(e.target.checked ? "active" : "disabled")} />
        Active
      </label>
      <div className="flex gap-2">
        <button data-testid={`broker-save-${broker.id}`}
          onClick={() => onSave({ name, website, logo_url: broker.logo_url || "", servers: servers.split(",").map((s) => s.trim()).filter(Boolean), status })}
          className="flex-1 bg-amber-400/90 text-ink-900 text-xs font-semibold rounded px-3 py-1.5 inline-flex items-center justify-center gap-1">
          <Save className="size-3.5" /> Save
        </button>
        <button data-testid={`broker-delete-${broker.id}`} onClick={onDelete}
          className="px-3 py-1.5 rounded border border-bear/40 text-bear text-xs inline-flex items-center gap-1">
          <Trash2 className="size-3.5" />
        </button>
      </div>
    </div>
  );
}
